import React, { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { AgGrid, AgGridReact, Flex, Grid } from "@jsq/ds/core";
import { AccountingPayableSlideout } from "../AccountingPayableSlideout";
import { type AccountingPayableData } from "../AccountingPayableSlideout/types";
import { getColumnDefinitions } from "./columnDefinitions";
import { tableContainer } from "./styles_vanilla.css";
import { TransactionsGraphqlDataSource } from "./transactionsGQLDataSource";
import { TransactionRowItem, TransactionsTableProps } from "./types";

const rowCountPerPage = 20;

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true, // Allows text to be copied from cells
  pagination: true,
  paginationPageSize: rowCountPerPage,
  paginationPageSizeSelector: false,
  rowHeight: 50,
  rowSelection: "single",
  suppressCellFocus: true,
  suppressContextMenu: true,
};

const defaultColumnOptions = {
  editable: false,
  flex: 1,
  minWidth: 100,
  sortable: false,
};

const sideBarDef: AgGrid.SideBarDef = {
  toolPanels: [
    {
      id: "filters",
      labelDefault: "Filters",
      labelKey: "filters",
      iconKey: "filter",
      toolPanel: "agFiltersToolPanel",
      minWidth: 280,
      width: 280,
    },
  ],
  position: "right",
};

/**
 * TransactionsTable - AG Grid table for the unified Accounting Payables list.
 */
export const TransactionsTable: React.FC<TransactionsTableProps> = ({
  paymentsContext,
  selectedTransactionId,
  onSelectTransaction,
  onCloseSlideout,
  onPayClick,
  onCreatePayment,
  onReviewClick,
  onPaymentBatchReviewClick,
  selectedPaymentBatchId,
  onReviewSlideoutClose,
}) => {
  const client = useApolloClient();

  const gridDataSource = useMemo<AgGrid.IServerSideDatasource>(() => {
    return new TransactionsGraphqlDataSource(client, rowCountPerPage);
  }, [client]);

  const handlePayClick = useCallback(
    (transaction: TransactionRowItem) => {
      onPayClick?.(transaction);
    },
    [onPayClick],
  );

  const handleCreatePayment = useCallback(
    (payable: AccountingPayableData) => {
      onCloseSlideout?.();
      onCreatePayment?.(payable);
    },
    [onCloseSlideout, onCreatePayment],
  );

  const columns = useMemo(
    () =>
      getColumnDefinitions({
        onPayClick: handlePayClick,
        onReviewClick,
      }),
    [handlePayClick, onReviewClick],
  );

  const handleRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<TransactionRowItem>) => {
      if (event.data) {
        onSelectTransaction?.(event.data);
      }
    },
    [onSelectTransaction],
  );

  return (
    <Flex className={tableContainer}>
      <Grid<TransactionRowItem>
        key="transactions_grid"
        cacheBlockSize={rowCountPerPage}
        columnDefs={columns}
        defaultColDef={defaultColumnOptions}
        getRowId={(params: AgGrid.GetRowIdParams<TransactionRowItem>) => params.data.id}
        gridId="transactions"
        gridOptions={gridOptions}
        onRowClicked={handleRowClicked}
        reactiveCustomComponents
        rowModelType="serverSide"
        serverSideDatasource={gridDataSource}
        sideBar={sideBarDef}
      />
      {selectedTransactionId && onCloseSlideout && (
        <AccountingPayableSlideout
          id={selectedTransactionId}
          isOpen={!!selectedTransactionId}
          onClose={onCloseSlideout}
          onCreatePayment={onCreatePayment ? handleCreatePayment : undefined}
          onReviewClick={onPaymentBatchReviewClick}
          paymentsContext={paymentsContext}
          selectedPaymentBatchId={selectedPaymentBatchId}
          onReviewSlideoutClose={onReviewSlideoutClose}
        />
      )}
    </Flex>
  );
};

export default TransactionsTable;
